import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as Sentry from '@sentry/react';
import { RouteComponentProps } from 'react-router-dom';
import styled from 'styled-components/macro';
import { RouteDefinitions } from 'routes';
import { useAuth } from 'shared/providers/AuthProvider';
import usePersistedState from 'shared/hooks/usePersistedState';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { publicClient } from 'shared/apollo/publicClient';
import { env } from 'env';
import { useAcceptUserInvitationMutation } from 'shared/types/graphql';
import { Link } from '@televet/kibble-ui/build/components/Link';
import { Spinner } from '@televet/kibble-ui/build/components/Spinner';
import { useTracking } from 'shared/providers/TrackingProvider';
import { LoginEvents } from 'shared/providers/TrackingProvider/EventNames';

enum InvitationSource {
  EmailTeamMemberInvited = 'EmailTeamMemberInvited',
  EmailIntegrationInProgress = 'EmailIntegrationInProgress',
  EmailIntegrationComplete = 'EmailIntegrationComplete',
}

const AcceptInvitation = ({
  match,
  location,
  history,
}: RouteComponentProps<{ invitationCode: string }>): JSX.Element => {
  const { invitationCode } = match.params;
  const invitationSource = useMemo(() => new URLSearchParams(location.search).get('source'), [location]);
  const { isAuthenticated, isLoggingOut, logout, loginWithToken } = useAuth();
  const [isCodeInvalid, setIsCodeInvalid] = useState(false);
  const { trackEvent } = useTracking();

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [invitationAuthToken, setInvitationAuthToken] = usePersistedState<string>(
    PersistedStateKeys.InvitationAuthToken,
    '',
  );

  const [acceptInvitation] = useAcceptUserInvitationMutation({
    client: publicClient,
    context: { headers: { authorization: `Bearer ${env.REACT_APP_PUBLIC_API_KEY}` } },
  });

  /**
   * On initial page load, if the user is authenticated, log them out.
   */
  const hasStartedInitialLogout = useRef(false);
  useEffect(() => {
    if (isAuthenticated && !hasStartedInitialLogout.current) {
      logout({ suppressNavigation: true });
    }
    hasStartedInitialLogout.current = true;
  }, [isAuthenticated, logout]);

  /**
   * Once the logout triggered from above is complete, accept the invitation and redirect to settings.
   */
  useEffect(() => {
    if (hasStartedInitialLogout.current && !isAuthenticated && !isLoggingOut) {
      acceptInvitation({ variables: { data: { invitationCode } } }).then(async ({ data }) => {
        const token = data?.acceptUserInvitation?.token;
        if (token) {
          try {
            await loginWithToken(token, false);
            setInvitationAuthToken(token);
            const source = Object.values(InvitationSource).includes(invitationSource as InvitationSource)
              ? invitationSource
              : InvitationSource.EmailTeamMemberInvited;
            trackEvent(LoginEvents.AcceptInvitation, { source: source || '' });
            history.push(RouteDefinitions.Index); // The default redirect will be set in useModularity
          } catch (error) {
            Sentry.captureException(error);
            console.error(error);
            window.location.href = RouteDefinitions.Logout;
          }
        } else {
          setIsCodeInvalid(true);
        }
      });
    }
  }, [
    isAuthenticated,
    isLoggingOut,
    invitationCode,
    invitationSource,
    history,
    acceptInvitation,
    loginWithToken,
    setInvitationAuthToken,
    trackEvent,
  ]);

  return (
    <FullScreenContainer>
      {isCodeInvalid ? (
        <MessageContainer>
          <div>That invitation link is no longer valid</div>
          <div>
            Never fear! We just sent a new, valid link to your email address. If you don&apos;t receive the email within
            a couple minutes or you&apos;re still unable to access your account, please contact the Otto Support Team at{' '}
            <a href="mailto:support@otto.vet?subject=Help%20with%20Flow%20User%20Invitation">support@otto.vet</a>.
          </div>
          <div>
            Already have an Otto account? <Link href={RouteDefinitions.Logout}>Log in</Link>
          </div>
        </MessageContainer>
      ) : (
        <Spinner />
      )}
    </FullScreenContainer>
  );
};

const MessageContainer = styled.div`
  padding: 25px;
  border: 1px solid rgb(232, 233, 233);
  border-radius: 8px;
  max-width: 600px;

  & > div:first-of-type {
    font-size: 18px;
    font-weight: 600;
  }

  & > div:not(:first-of-type) {
    margin-top: 24px;
  }

  & > div:nth-of-type(2) {
    margin-top: 16px;
  }

  & a {
    color: hsl(190deg 43% 38%);
  }
`;

const FullScreenContainer = styled.div`
  height: 100vh;
  width: 100vw;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f8f8;

  & > div:not(${MessageContainer}) {
    padding: 0;
  }
`;

export default AcceptInvitation;
